import classNames from 'classnames';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { required } from 'redux-form-validators';
import { Field, reduxForm, formValueSelector, initialize, touch } from 'redux-form';
import { isValidNumber } from 'libphonenumber-js';
import { compose } from 'redux';
import React, { useState, useEffect } from 'react';

import { FormMessageHOC } from 'common';
import {
  ReduxSelectField,
  ReduxTextField,
  PhoneField,
  CheckboxField,
  DateField,
  DefaultButton,
} from 'common/components';
import { withTheme } from 'common/styling/theme';
import SaveChangesModal from '../SaveChangesModal/SaveChangesModal';

import { sendKYC } from '../../_redux';
import { saveKYCToRedis, setKYCStatusToRedis, getKYCFromRedis, getKYCStatusFromRedis } from '../../api';
import { ExperienceFields, SelectCountryField } from './fields';

import { staticStyles, getDynamicStyles } from './style';

const validatePhoneNumber = phoneNumber => {
  if (!isValidNumber(phoneNumber)) {
    return <FormattedMessage id="validationErrorWrongPhone" />;
  }
};

const KYCForm = ({
  handleSubmit,
  submitting,
  invalid,
  error,
  user,
  countries,
  states,
  countriesAreLoaded,
  statesAreLoaded,
  experienceForex,
  experienceDerivative,
  experienceSecurities,
  experienceInvestmentFund,
  removeKycTaxCheckbox,
  personalIdInKycForm,
  dispatch,
  intl,
  theme,
  pristine,
  history,
  onFormStateChange,
  formValues,
}) => {
  const submit = handleSubmit(formValues => {
    if (removeKycTaxCheckbox) {
      formValues = { ...formValues, usTax: true };
    }
    return sendKYC(formValues, dispatch);
  });

  // 模态框状态
  const [modalOpen, setModalOpen] = useState(false);

  // 使用useRef来跟踪初始化状态，这样不会触发重新渲染
  const hasBeenInitializedRef = React.useRef(false);

  // 使用useRef存储handleRoundBackButtonClick函数的引用，确保传递给父组件的是同一个引用
  const handleRoundBackButtonClickRef = React.useRef(null);

  // 组件初始化时从Redis加载暂存数据
  useEffect(() => {
    // 只有当尚未初始化时才执行
    if (!hasBeenInitializedRef.current) {
      const loadTemporaryData = async () => {
        try {
          // 检查用户是否有暂存的数据
          const statusResponse = await getKYCStatusFromRedis();
          const status = statusResponse.data.status;

          // 如果状态为暂存，则加载数据
          if (status === 'temporary_saved') {
            const response = await getKYCFromRedis();
            const temporaryData = response.data;

            // 如果有暂存数据，则使用Redux Form的initialize action creator填充到表单中
            if (temporaryData && Object.keys(temporaryData).length > 0) {
              // 使用setTimeout将dispatch放入下一个事件循环，避免立即触发重新渲染
              setTimeout(() => {
                dispatch(initialize('KYCForm', temporaryData));
              }, 0);
            }
          }
          // 无论是否加载了数据，都标记为已初始化
          hasBeenInitializedRef.current = true;
        } catch (error) {
          // console.error('Error loading temporary KYC dat:a:', error);
          // 加载失败也标记为已初始化，避免重试
          hasBeenInitializedRef.current = true;
        }
      };

      // 尝试加载暂存数据
      loadTemporaryData();
    }
  }, [dispatch]);

  // 使用useCallback包装处理函数
  const handleRoundBackButtonClick = React.useCallback(() => {
    // 如果表单有未保存的更改，显示模态框
    if (!pristine) {
      setModalOpen(true);
    } else {
      // 否则根据路由回退
      history.goBack();
    }
  }, [pristine, history, setModalOpen]);

  // 使用useEffect将处理函数赋值给ref，确保在React生命周期内正确执行
  useEffect(() => {
    // 当handleRoundBackButtonClick函数变化时，更新ref
    handleRoundBackButtonClickRef.current = handleRoundBackButtonClick;
  }, [handleRoundBackButtonClick]);

  // 存储上一次传递给父组件的表单状态
  const prevFormStateRef = React.useRef(null);

  // 将表单状态和处理函数传递给父组件
  // 添加状态比较逻辑，确保只有在状态实际变化时才调用onFormStateChange，避免无限循环
  useEffect(() => {
    if (onFormStateChange && handleRoundBackButtonClickRef.current) {
      const currentFormState = {
        pristine,
        submitting,
        invalid,
        onRoundBackButtonClick: handleRoundBackButtonClickRef.current,
      };

      // 检查状态是否发生了实际变化
      // 仅比较基本类型字段和函数引用
      const isStateChanged =
        !prevFormStateRef.current ||
        prevFormStateRef.current.pristine !== pristine ||
        prevFormStateRef.current.submitting !== submitting ||
        prevFormStateRef.current.invalid !== invalid ||
        prevFormStateRef.current.onRoundBackButtonClick !== currentFormState.onRoundBackButtonClick;

      if (isStateChanged) {
        onFormStateChange(currentFormState);
        // 更新上一次的状态引用
        prevFormStateRef.current = currentFormState;
      }
    }
  }, [pristine, submitting, invalid, onFormStateChange]);

  // 使用useCallback包装hasAllRequiredFields函数
  const hasAllRequiredFields = React.useCallback(formValues => {
    // 检查基本必填字段
    const requiredFields = [
      'firstName',
      'lastName',
      'taxResidence',
      'birthday',
      'nationality',
      'phone',
      'address.country',
      'address.state',
      'address.city',
      'address.address',
      'address.postCode',
      'finance.capital',
      'finance.income',
      'finance.commitments',
    ];

    // 检查经验部分的必填字段
    const hasExperience =
      formValues.experience?.forex?.checkbox ||
      formValues.experience?.derivative?.checkbox ||
      formValues.experience?.securities?.checkbox ||
      formValues.experience?.investmentFund?.checkbox;

    if (hasExperience) {
      requiredFields.push('experience.purpose', 'experience.employment', 'experience.source');
    }

    // 检查所有必填字段是否都有值
    return requiredFields.every(field => {
      // 处理嵌套字段
      const fields = field.split('.');
      let value = formValues;

      // 使用数组的every方法代替for...of循环
      return fields.every(f => {
        if (!value || value[f] === undefined || value[f] === null || value[f] === '') {
          return false;
        }
        value = value[f];
        return true;
      });
    });
  }, []);

  // 处理按钮提交操作 - 提交表单并设置状态为已提交
  const handleButtonSubmit = React.useCallback(
    async e => {
      e.preventDefault();

      // 如果表单有验证错误，触发所有字段的验证显示
      if (invalid) {
        // 定义所有需要验证的字段名称
        const allFields = [
          'firstName',
          'middleName',
          'lastName',
          'taxResidence',
          'birthday',
          'personalId',
          'nationality',
          'phone',
          'usTax',
          'address.country',
          'address.state',
          'address.city',
          'address.address',
          'address.postCode',
          'finance.capital',
          'finance.income',
          'finance.commitments',
          'finance.investments',
          'finance.purpose',
          'activity.employment',
          'activity.source',
          'activity.business',
          'activity.education',
          'experience.forex.checkbox',
          'experience.forex.frequency',
          'experience.forex.volume',
          'experience.forex.duration',
          'experience.derivative.checkbox',
          'experience.derivative.frequency',
          'experience.derivative.volume',
          'experience.derivative.duration',
          'experience.securities.checkbox',
          'experience.securities.frequency',
          'experience.securities.volume',
          'experience.securities.duration',
          'experience.investmentFund.checkbox',
          'experience.investmentFund.frequency',
          'experience.investmentFund.volume',
          'experience.investmentFund.duration',
        ];

        // 使用dispatch触发所有字段的touch状态，激活验证显示
        dispatch(touch('KYCForm', ...allFields));

        // 等待DOM更新后再查找错误字段
        setTimeout(() => {
          // 查找第一个包含错误的表单字段（通过错误文本位置）
          const firstErrorField = document.querySelector(
            '.ReduxTextField__error:not(:empty), .ReduxSelectField__error:not(:empty), .PhoneField__error:not(:empty), .DateField__error:not(:empty), .CheckboxField__error:not(:empty)'
          );

          if (firstErrorField) {
            // 滚动到第一个错误字段
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // 尝试聚焦到该字段的输入元素（向上查找最近的输入元素）
            const fieldContainer = firstErrorField.closest(
              '.BaseTextField, .BaseSelectField, .PhoneField, .DateField, .CheckboxField'
            );
            if (fieldContainer) {
              const inputElement = fieldContainer.querySelector('input, select, textarea');
              if (inputElement) {
                setTimeout(() => {
                  inputElement.focus();
                }, 500); // 等待滚动完成后再聚焦
              }
            }
          }
        }, 100);

        return; // 阻止提交
      }

      if (!submitting) {
        try {
          // 提交表单保存数据
          await submit();
          // 设置状态为已提交
          await setKYCStatusToRedis('submitted');
        } catch (error) {
          // console.error('Error submitting KYC form:', error);
          // 错误处理已在submit函数中完成
        }
      }
    },
    [invalid, submitting, formValues, hasAllRequiredFields, submit, setKYCStatusToRedis]
  );

  // 处理保存操作 - 提交完整表单
  const handleSave = React.useCallback(async () => {
    try {
      // 从props中获取当前表单值
      const currentFormValues = formValues;

      // 保存到Redis
      await saveKYCToRedis(currentFormValues);

      // 设置状态为暂存
      await setKYCStatusToRedis('temporary_saved');

      // 关闭模态框并返回上一页
      setModalOpen(false);
      history.goBack();
    } catch (error) {
      // console.error('Error saving KYC data temporarily:', error);
      // alert('Failed to save changes temporarily. Please try again.');
    }
    setModalOpen(false);
  }, [
    invalid,
    submitting,
    formValues,
    hasAllRequiredFields,
    submit,
    setKYCStatusToRedis,
    history,
    setModalOpen,
    saveKYCToRedis,
  ]);

  // 处理不保存操作
  const handleDontSave = React.useCallback(() => {
    setModalOpen(false);
    history.goBack();
  }, [setModalOpen, history]);

  // 处理关闭模态框
  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const countriesOptions = countries
    .map(option => ({
      value: option.gid,
      label: option.name,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const statesOptions = states
    .map(state => ({
      value: state.gid,
      label: state.name,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const generateOptionsFromTranslations = keys =>
    keys.map(key => ({ value: key, label: intl.formatMessage({ id: key }) }));
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const isEditable = user.status === 'incomplete' || user.status === 'unverified';
  const disabled = !isEditable || submitting;

  return (
    <form className="KYCForm" onSubmit={submit}>
      <div className="KYCForm__inputs-inner">
        <FormattedMessage id="generalInfo">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="KYCForm__inputs-block">
          <Field
            component={ReduxTextField}
            type="text"
            name="firstName"
            textId="kycFirstName"
            disabled={disabled}
            validate={[required()]}
          />
          <Field component={ReduxTextField} type="text" name="middleName" textId="kycMiddleName" disabled={disabled} />
          <Field
            component={ReduxTextField}
            type="text"
            name="lastName"
            textId="kycLastName"
            disabled={disabled}
            validate={[required()]}
          />
        </div>

        <div className={classNames('KYCForm__inputs-block', { 'block-two-inp': !personalIdInKycForm })}>
          <Field
            component={ReduxSelectField}
            name="taxResidence"
            textId="kycTaxResidence"
            options={countriesOptions}
            disabled={disabled}
            validate={[required()]}
            searchable
          />
          <Field
            component={DateField}
            type="date"
            name="birthday"
            textId="kycBirthday"
            placeholder="dd.mm.yyyy"
            disabled={disabled}
            validate={[required()]}
          />
          {personalIdInKycForm && (
            <Field
              component={ReduxTextField}
              type="text"
              name="personalId"
              textId="kycPassportNumber"
              disabled={disabled}
            />
          )}
        </div>

        <div className="KYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="nationality"
            textId="kycNationality"
            options={countriesOptions}
            disabled={disabled}
            validate={[required()]}
            searchable
          />
          <Field
            component={PhoneField}
            name="phone"
            textId="kycPhone"
            disabled={disabled || user.phoneConfirmed}
            validate={[required(), validatePhoneNumber]}
          />
        </div>

        {!removeKycTaxCheckbox && (
          <Field component={CheckboxField} name="usTax" textId="kycUsTax" disabled={disabled} />
        )}
      </div>
      <div className="KYCForm__inputs-inner">
        <FormattedMessage id="kycAddress">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="KYCForm__inputs-block block-two-inp">
          <Field
            component={SelectCountryField}
            name="address.country"
            textId="kycCountry"
            options={countriesOptions}
            disabled={disabled || !countriesAreLoaded}
            validate={[required()]}
          />
          <Field
            component={ReduxSelectField}
            name="address.state"
            textId="kycState"
            options={statesOptions}
            disabled={disabled || !statesAreLoaded}
            validate={[required()]}
            searchable
          />
        </div>

        <div className="KYCForm__inputs-block">
          <Field
            component={ReduxTextField}
            type="text"
            name="address.city"
            textId="kycCity"
            disabled={disabled}
            validate={[required()]}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="address.address"
            textId="kycAddress"
            disabled={disabled}
            validate={[required()]}
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="address.postCode"
            textId="kycPostCode"
            disabled={disabled}
            validate={[required()]}
          />
        </div>
      </div>

      <div className="KYCForm__inputs-inner">
        <FormattedMessage id="kycFinDetails">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="KYCForm__inputs-block">
          <Field
            component={ReduxSelectField}
            name="finance.capital"
            textId="kycNetCapital"
            options={generateOptionsFromTranslations([
              'kycAmountBelow10',
              'kycAmount10to50',
              'kycAmount50to100',
              'kycAmountOver100',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
          <Field
            component={ReduxSelectField}
            name="finance.income"
            textId="kycAnnualIncome"
            options={generateOptionsFromTranslations([
              'kycAmountBelow10',
              'kycAmount10to50',
              'kycAmount50to100',
              'kycAmountOver100',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
          <Field
            component={ReduxSelectField}
            name="finance.commitments"
            textId="kycMonFinCommit"
            options={generateOptionsFromTranslations([
              'kycCommitmentsBelow20',
              'kycCommitments21to40',
              'kycCommitments41to60',
              'kycCommitments61to80',
              'kycCommitmentsOver80',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
        </div>

        <div className="KYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxTextField}
            type="text"
            disabled={disabled}
            name="finance.investments"
            textId="kycApproxInvestPerAnn"
            validate={[required()]}
          />
          <Field
            component={ReduxSelectField}
            name="finance.purpose"
            textId="kycPurpose"
            options={generateOptionsFromTranslations([
              'kycPurposeInvestment',
              'kycPurposeHedging',
              'kycPurposeSpecTrading',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
        </div>
      </div>
      <div className="KYCForm__inputs-inner">
        <FormattedMessage id="kycTypeOfActivity">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="KYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="activity.employment"
            textId="kycEmplStatus"
            options={generateOptionsFromTranslations([
              'kycEmploymentEmpl',
              'kycEmploymentUnempl',
              'kycEmploymentSelfEmpl',
              'kycEmploymentRet',
              'kycEmploymentStud',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
          <Field
            component={ReduxSelectField}
            name="activity.source"
            textId="kycSourceOfFunds"
            options={generateOptionsFromTranslations([
              'kycSourceProfAct',
              'kycSourceSavings',
              'kycSourceFam',
              'kycSourceInvest',
              'kycSourceBorrow',
              'kycSourcePension',
              'kycSourceOther',
            ])}
            disabled={disabled}
            validate={[required()]}
          />
        </div>

        <div className="KYCForm__inputs-block block-two-inp">
          <Field
            component={ReduxSelectField}
            name="activity.business"
            textId="kycNatureOfBusiness"
            options={generateOptionsFromTranslations([
              'kycBizAccount',
              'kycBizSecr',
              'kycBizAgric',
              'kycBizArt',
              'kycBizBank',
              'kycBizCatHosp',
              'kycBizMedia',
              'kycBizEdu',
              'kycBizEmerg',
              'kycBizEngin',
              'kycBizHealth',
              'kycBizIT',
              'kycBizLegal',
              'kycBizLeisure',
              'kycBizManuf',
              'kycBizPR',
              'kycBizProp',
              'kycBizSales',
              'kycBizSocial',
              'kycBizTelecom',
              'kycBizTransport',
              'kycBizOther',
            ])}
            disabled={disabled}
            validate={[required()]}
            searchable
          />
          <Field
            component={ReduxSelectField}
            name="activity.education"
            textId="kycLvlOfEdu"
            options={generateOptionsFromTranslations(['kycEduHS', 'kycEduColl', 'kycEduUnivers', 'kycEduOther'])}
            disabled={disabled}
            validate={[required()]}
          />
        </div>
      </div>
      <div className="KYCForm__inputs-inner">
        <FormattedMessage id="kycTradingExp">{txt => <h2>{txt}</h2>}</FormattedMessage>

        <div className="KYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.forex"
            textId="kycForexCdfs"
            disabled={disabled}
            active={experienceForex}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="KYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.derivative"
            textId="kycDerivativeInstr"
            disabled={disabled}
            active={experienceDerivative}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="KYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.securities"
            textId="kycTransfSec"
            disabled={disabled}
            active={experienceSecurities}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>

        <div className="KYCForm__inputs-block block-experience">
          <ExperienceFields
            prefix="experience.investmentFund"
            textId="kycUnitsCollectiveInv"
            disabled={disabled}
            active={experienceInvestmentFund}
            generateOptionsFromTranslations={generateOptionsFromTranslations}
          />
        </div>
      </div>
      {!isEditable && (
        <div className="KYCForm__inputs-inner KYCForm__text">
          <FormattedMessage
            id="ifUserSettingIsChange"
            values={{
              support: (
                <Link to="/support/" id="cleint-KYCForm-Link-to-support">
                  <FormattedMessage id="contactCustomerSupport" />
                </Link>
              ),
            }}
          />
        </div>
      )}

      {isEditable && (
        <div className="KYCForm__buttons">
          <DefaultButton
            textId="sendToVerify"
            type="button"
            onClick={handleButtonSubmit}
            disabled={submitting}
            loading={submitting}
            filled
          />
        </div>
      )}

      {/* 保存更改确认模态框 */}
      <SaveChangesModal isOpen={modalOpen} onSave={handleSave} onDontSave={handleDontSave} onClose={handleCloseModal} />
      {error && <strong className="formError">{error}</strong>}
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </form>
  );
};

KYCForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  experienceForex: PropTypes.bool.isRequired,
  experienceDerivative: PropTypes.bool.isRequired,
  experienceSecurities: PropTypes.bool.isRequired,
  experienceInvestmentFund: PropTypes.bool.isRequired,
  error: PropTypes.string,
  countries: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  countriesAreLoaded: PropTypes.bool.isRequired,
  statesAreLoaded: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  removeKycTaxCheckbox: PropTypes.bool.isRequired,
  personalIdInKycForm: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  theme: PropTypes.object,
  history: PropTypes.object.isRequired,
  onFormStateChange: PropTypes.func,
  formValues: PropTypes.object.isRequired,
};

KYCForm.defaultProps = {
  error: '',
  theme: {},
  onFormStateChange: () => {},
};

// 创建formValueSelector来获取整个表单的值
const selector = formValueSelector('KYCForm');

const mapStateToProps = state => ({
  experienceForex: selector(state, 'experience.forex.checkbox') || false,
  experienceDerivative: selector(state, 'experience.derivative.checkbox') || false,
  experienceSecurities: selector(state, 'experience.securities.checkbox') || false,
  experienceInvestmentFund: selector(state, 'experience.investmentFund.checkbox') || false,
  removeKycTaxCheckbox: state.interfaceConfig.features.includes('removeKycTaxCheckbox'),
  personalIdInKycForm: state.interfaceConfig.features.includes('personalIdInKycForm'),
  // 使用getFormValues来获取整个表单的值
  formValues: state.form.KYCForm?.values || {},
});

const ThemedKYCForm = compose(
  withRouter,
  withTheme(),
  FormMessageHOC,
  injectIntl,
  connect(mapStateToProps),
  reduxForm({
    form: 'KYCForm',
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage();
      // 重置表单状态，使pristine变为true
      dispatch(initialize('KYCForm', props.formValues));
      // 导航到documents页面
      props.history.push('/documents');
    },
  })
)(KYCForm);

export default ThemedKYCForm;
export { KYCForm };
